import React, { createContext, useContext, useEffect, useState } from "react";
import {
  loginWithEmailAndPassword,
  logoutUser,
  resetPassword,
  signUpWithEmailAndPassword,
  getCurrentUser,
  updateUserCurrency,
  resetUserAccount,
  getUserTransactions,
} from "@/services/auth";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "@/api/firebase/firebase";
import { addToast } from "@heroui/react";
import { User, UserRole, TransactionData } from "@/types/user";
import { FirebaseError } from "@/types/errors";
import LoadingScreen from "@/components/LoadingScreen";
import { resetSessionId } from "@/utils/session";

type AuthContextType = {
  currentUser: User | null;
  userRole: UserRole | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (
    email: string,
    password: string,
    name: string
  ) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  setCurrentUser: (user: User | null) => void;
  updateCurrency: (currency: string) => Promise<void>;
  resetAccount: () => Promise<void>;
  getTransactions: (
    viewModeOverride?: "default" | "custom"
  ) => Promise<TransactionData[]>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);

  async function login(email: string, password: string) {
    try {
      const user = await loginWithEmailAndPassword(email, password);
      setCurrentUser(user);
      setUserRole(user.role);
    } catch (error: unknown) {
      const firebaseError = error as FirebaseError;
      throw firebaseError;
    }
  }

  async function signup(
    email: string,
    password: string,
    name: string
  ) {
    try {
      const user = await signUpWithEmailAndPassword(
        email,
        password,
        name
      );
      setCurrentUser(user);
      setUserRole(user.role);
      addToast({
        description: "Account created successfully!",
      });
    } catch (error: unknown) {
      const firebaseError = error as FirebaseError;
      throw firebaseError;
    }
  }

  async function logout() {
    try {
      await logoutUser();
      // Reset session ID to prevent document ID collisions when a different user logs in
      resetSessionId();
      setCurrentUser(null);
      setUserRole(null);
    } catch (error: unknown) {
      const firebaseError = error as FirebaseError;
      throw firebaseError;
    }
  }

  async function sendResetPassword(email: string) {
    try {
      await resetPassword(email);
      addToast({
        description: "Password reset email sent!",
      });
    } catch (error: unknown) {
      const firebaseError = error as FirebaseError;
      throw firebaseError;
    }
  }

  async function updateCurrency(currency: string) {
    try {
      await updateUserCurrency(currency);
      // Update the local user state
      if (currentUser) {
        setCurrentUser({ ...currentUser, currency });
      }
      addToast({
        description: "Currency preference updated successfully!",
      });
    } catch (error: unknown) {
      const firebaseError = error as FirebaseError;
      throw firebaseError;
    }
  }

  async function resetAccount() {
    try {
      await resetUserAccount();
      // Update the local user state
      if (currentUser) {
        setCurrentUser({ ...currentUser, currency: undefined });
      }
      addToast({
        description: "Account reset successfully!",
      });
    } catch (error: unknown) {
      const firebaseError = error as FirebaseError;
      throw firebaseError;
    }
  }

  async function getTransactions(viewModeOverride?: "default" | "custom") {
    try {
      return await getUserTransactions(viewModeOverride);
    } catch (error: unknown) {
      const firebaseError = error as FirebaseError;
      throw firebaseError;
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);

      if (firebaseUser) {
        try {
          // Get user data from Firestore using the new service
          const user = await getCurrentUser();
          if (user) {
            setCurrentUser(user);
            setUserRole(user.role);
          } else {
            // If user exists in Firebase Auth but not in Firestore, clear auth
            setCurrentUser(null);
            setUserRole(null);
          }
        } catch (error) {
          // Auth token may not have propagated to Firestore yet — retry once
          try {
            await new Promise((r) => setTimeout(r, 1000));
            const user = await getCurrentUser();
            if (user) {
              setCurrentUser(user);
              setUserRole(user.role);
            } else {
              setCurrentUser(null);
              setUserRole(null);
            }
          } catch {
            setCurrentUser(null);
            setUserRole(null);
          }
        }
      } else {
        setCurrentUser(null);
        setUserRole(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userRole,
    loading,
    login,
    signup,
    logout,
    resetPassword: sendResetPassword,
    setCurrentUser,
    updateCurrency,
    resetAccount,
    getTransactions,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <LoadingScreen /> : children}
    </AuthContext.Provider>
  );
}
